import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AdminLogin from '../components/AdminLogin';
import AdminArticles from '../components/admin/AdminArticles';
import AdminAuthors from '../components/admin/AdminAuthors';
import AdminOverview from '../components/admin/AdminOverview';
import AdminSettings from '../components/admin/AdminSettings';
import AdminSources from '../components/admin/AdminSources';
import useNews from '../hooks/useNews';
import {
  getAdminSession,
  signOutAdmin,
  subscribeToAdminSession
} from '../services/adminAuthService';
import {
  fetchRssSources,
  runRssIngestion
} from '../services/newsService';
import '../styles/admin.css';

const {
  FiBarChart2,
  FiFileText,
  FiLogOut,
  FiRadio,
  FiRefreshCw,
  FiSettings,
  FiUsers
} = FiIcons;

function Admin() {
  const [session, setSession] = useState(undefined);
  const [tab, setTab] = useState('Overview');
  const [rssSources, setRssSources] = useState([]);
  const [sync, setSync] = useState({
    loading: false,
    message: '',
    error: ''
  });
  const { articles, refresh } = useNews({ limit: 250 });

  const checkSession = useCallback(async () => {
    setSession(await getAdminSession());
  }, []);

  const loadSources = useCallback(async () => {
    try {
      setRssSources(await fetchRssSources());
    } catch (error) {
      setSync((current) => ({
        ...current,
        error: error.message || 'Unable to load RSS sources.'
      }));
    }
  }, []);

  useEffect(() => {
    checkSession();
    return subscribeToAdminSession(checkSession);
  }, [checkSession]);

  useEffect(() => {
    if (session) loadSources();
  }, [session, loadSources]);

  if (session === undefined) {
    return <div className="admin-loading">Opening newsroom…</div>;
  }

  if (!session) {
    return <AdminLogin onAuthenticated={checkSession} />;
  }

  const tabs = [
    ['Overview', FiBarChart2],
    ['Articles', FiFileText],
    ['RSS Sources', FiRadio],
    ['Authors', FiUsers],
    ['Settings', FiSettings]
  ];

  const syncFeeds = async () => {
    setSync({ loading: true, message: '', error: '' });

    try {
      const result = await runRssIngestion();
      await Promise.all([refresh(), loadSources()]);
      setSync({
        loading: false,
        message: `Import completed: ${result.imported || 0} new articles from ${result.processed || 0} sources.`,
        error: ''
      });
    } catch (error) {
      setSync({
        loading: false,
        message: '',
        error: `${error.message} Confirm that the rss-ingest Edge Function is deployed in Supabase.`
      });
    }
  };

  const logout = async () => {
    await signOutAdmin();
    setSession(null);
  };

  const stats = [
    ['Total Articles', articles.length],
    [
      'RSS Articles',
      articles.filter((item) => item.label === 'RSS / SOURCE').length
    ],
    [
      'Original Articles',
      articles.filter((item) => item.label !== 'RSS / SOURCE').length
    ],
    [
      'Total Views',
      articles.reduce((total, item) => total + item.viewCount, 0)
    ],
    [
      'Active Sources',
      rssSources.filter((source) => source.active).length
    ]
  ];

  const panels = {
    Overview: (
      <AdminOverview articles={articles} sources={rssSources} />
    ),
    Articles: <AdminArticles articles={articles} />,
    'RSS Sources': <AdminSources sources={rssSources} />,
    Authors: <AdminAuthors articles={articles} />,
    Settings: <AdminSettings session={session} />
  };

  return (
    <div className="admin-shell">
      <aside className="admin-nav">
        <div className="admin-wordmark">
          Opini<span>Guru</span>
        </div>
        <small>NEWSROOM</small>
        <nav>
          {tabs.map(([name, icon]) => (
            <button
              className={tab === name ? 'active' : ''}
              onClick={() => setTab(name)}
              key={name}
            >
              <SafeIcon icon={icon} />
              {name}
            </button>
          ))}
        </nav>
        <Link to="/">← View publication</Link>
        <button className="admin-signout" onClick={logout}>
          <SafeIcon icon={FiLogOut} /> Sign out
        </button>
      </aside>

      <main className="admin-main">
        <header className="admin-topbar">
          <div>
            <span>OPINIGURU NEWSROOM</span>
            <h1>{tab}</h1>
            <p>
              Monitor publication content and automated reporting
              sources.
            </p>
          </div>
          <div className="admin-user">
            <span>{session.user.email}</span>
            <button
              className="admin-primary"
              onClick={syncFeeds}
              disabled={sync.loading}
            >
              <SafeIcon icon={FiRefreshCw} />
              {sync.loading ? 'Importing…' : 'Import RSS now'}
            </button>
          </div>
        </header>

        {sync.message && (
          <p className="admin-notice success">{sync.message}</p>
        )}
        {sync.error && (
          <p className="admin-notice error">{sync.error}</p>
        )}

        <section className="admin-stats">
          {stats.map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <b>{Number(value).toLocaleString()}</b>
              <small>Live Supabase data</small>
            </div>
          ))}
        </section>

        {panels[tab]}
      </main>
    </div>
  );
}

export default Admin;